import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { LayoutUtilsService, MessageType } from '../../../../../../core/_base/crud';
import { StringUtilsService } from '../../../../../../core/services/string-utils.service';
import { AppState } from '../../../../../../core/reducers';
import { BehaviorSubject, fromEvent, Observable } from 'rxjs';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { RelationshipCategoryService } from '../../../../../../core/lists/_services/relationship-category.service';
import { GeneralAutocompleteComponent } from '../../../../../partials/layout/general-autocomplete/general-autocomplete.component';
import { CategoryService } from '../../../../../../core/lists/_services';
import { AuthService } from '../../../../../../core/auth';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { QrveyService } from '../../../../../../core/lists/_services/qrvey.service';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs/operators';

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'kt-internal-access-edit',
  templateUrl: './internal-access-edit.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InternalAccessEditComponent implements OnInit, OnDestroy, AfterViewInit {
  companyId: number;
  loadingSubject = new BehaviorSubject<boolean>(true);
  loading$: Observable<boolean>;
  internalAccessForm: FormGroup;
  hasFormErrors = false;
  submissionErrorObj: any;
  hasSubmissionErrors: boolean;
  subscriptions = [];
  allPages = [];
  optionPages = [];
  selectedPages = [];
  appId = null;
  rule: string;
  categoryAutocompleteCall = this.categoryService.getAllCategories.bind(this.categoryService);
  @ViewChild('categoryAutocomplete') categoryAutocomplete: GeneralAutocompleteComponent;
  @ViewChild('pagess', { static: true }) pagess: ElementRef;
  hasErrorConflict = false;

  constructor(
    private categoryService: CategoryService,
    public dialogRef: MatDialogRef<InternalAccessEditComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private store: Store<AppState>,
    private router: Router,
    private internalAccessFB: FormBuilder,
    private relationshipCategoryService: RelationshipCategoryService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
    private authService: AuthService,
    public dialog: MatDialog,
    private layoutUtilsService: LayoutUtilsService,
    public stringUtils: StringUtilsService,
    private activatedRoute: ActivatedRoute,
    private qrveyService: QrveyService,
  ) {
    this.activatedRoute.queryParams.subscribe((params) => {
      if (params.app_id) {
        this.appId = params.app_id;
      }
    });
  }

  ngOnInit() {
    const searchSubscription = fromEvent(this.pagess.nativeElement, 'keyup')
      .pipe(
        debounceTime(150),
        distinctUntilChanged(),
        tap(() => {
          this.pagesAutoComplete(this.pagess.nativeElement.value);
        }),
      )
      .subscribe();
    this.subscriptions.push(searchSubscription);
    this.qrveyService.getPageGroups(this.appId).subscribe((res) => {
      this.allPages = res.data;
      this.optionPages = this.allPages;
      this.cdr.detectChanges();
    });
    this.createForm();
  }

  getTitle() {
    if (this.data.mode === 'CREATE') return 'New Internal Access';
    return 'Update Internal Access';
  }

  ngOnDestroy() {
    this.cdr.detach();
    this.subscriptions.forEach((el) => {
      if (el.sub) {
        el.sub.unsubscribe();
      }
    });
  }

  ngAfterViewInit(): void {
    if (this.data.mode === 'UPDATE') {
      this.rule = this.data._item.qrvey_user_role ? this.data._item.qrvey_user_role : null;
      const teams = {
        id: this.data._item?.team_id,
        name: this.data._item?.team_name,
      };
      this.categoryAutocomplete.setValue(teams ? teams : null);
      this.cdr.detectChanges();
    }
  }

  createForm() {
    const teams = {
      id: this.data._item?.team_id,
      name: this.data._item?.team_name,
    };
    this.selectedPages = this.data._item && this.data._item.groups ? this.data._item.groups : [];
    this.internalAccessForm = this.internalAccessFB.group({
      teams: [this.data._item && this.data._item.team_id ? teams : null, Validators.required],
      pages: [
        this.data._item && this.data._item.groups ? this.data._item.groups : null,
        Validators.required,
      ],
    });
  }

  onSubmit() {
    this.categoryAutocomplete.updateValueAndValidity();
    if (!this.rule) {
      return;
    }
    this.hasFormErrors = false;
    const controls = this.internalAccessForm.controls;
    if (this.internalAccessForm.invalid) {
      Object.keys(controls).forEach((controlName) => controls[controlName].markAsTouched());
      this.hasFormErrors = true;
      return;
    }
    this.addPageGroupExternal(this.preparePageGroupExternal());
  }

  preparePageGroupExternal() {
    const controls = this.internalAccessForm.controls;
    const pageGroup = {
      id: this.data._item && this.data._item.id ? this.data._item.id : null,
      team_id:
        this.data._item && this.data._item.team_id
          ? this.data._item.team_id
          : controls.teams?.value?.id,
      role: this.rule,
      user_type: 'USER',
      name: controls.teams.value.name,
      page_groups: controls.pages.value,
    };
    return pageGroup;
  }

  addPageGroupExternal(_item) {
    this.qrveyService.putCategoryAccess(_item, this.appId).subscribe(
      () => {
        this.layoutUtilsService.showActionNotification(
          'Access Added Successfully',
          MessageType.Update,
          3000,
          false,
          false,
        );
        this.dialogRef.close();
      },
      () => {
        this.hasErrorConflict = true;
        this.cdr.detectChanges();
      },
    );
  }

  isControlHasError(controlName: string, validationType: string): boolean {
    const control = this.internalAccessForm.controls[controlName];
    if (!control) {
      return false;
    }
    return control.hasError(validationType) && (control.dirty || control.touched);
  }

  close() {
    this.dialogRef.close();
  }

  onCategoryChange(val) {
    this.internalAccessForm.get('teams').setValue(val);
    this.internalAccessForm.controls.teams.setValue(val);
    this.internalAccessForm.controls.teams.updateValueAndValidity();
  }

  pageFn(event: any): string {
    return event ? event.name : '';
  }

  private pagesAutoComplete(term: string) {
    let _term = '' + term;
    _term = _term.trim();
    this.optionPages = this.allPages.filter((item) => item.name.includes(_term));
    this.cdr.detectChanges();
    return;
  }

  remove(p): void {
    const objWithIdIndex = this.selectedPages.findIndex((obj) => obj.id === p.id);
    this.selectedPages.splice(objWithIdIndex, 1);

    this.internalAccessForm.get('pages').setValue(this.selectedPages);
    this.cdr.detectChanges();
  }

  selected1(event: MatAutocompleteSelectedEvent): void {
    this.selectedPages.push(event.option.value);
    this.pagess.nativeElement.value = '';
    this.internalAccessForm.get('pages').setValue(this.selectedPages);
    this.cdr.detectChanges();
  }

  isSelected(p: any) {
    if (this.selectedPages?.indexOf(p) !== -1) return true;
    return false;
  }
}
